import _ from 'lodash';

import { getMountingRailChains } from '../common';
import geometry from '../../../../scene/util/geometry';
import productService, { getProduct } from '../../../../services/products';
import constants from '../../../../settings/constants';
import { flatten } from '../../../../util/array';
import { floor } from '../../../../util/round';
import tacHelpers from '../../tacHelpers';
import { ITEMS } from '../../../../constants';

const SHORT_RAIL_WIDTH = 650;
const LONG_RAIL_WIDTH = 1250;
const WIDTH_DIFF_ON_MERGE = 2 * SHORT_RAIL_WIDTH - LONG_RAIL_WIDTH; //50
const SECTION_WIDTH = 610;
const MOUNTING_RAIL_Y_POS = 1975;

/* assorted helper functions for getAdjustedMountingRails2 */

function getColorCount(sections) {
  return sections.reduce(
    (acc, curr) => {
      curr.items
        .filter(item =>
          productService.isType(item, ['end-shelf', 'side-panel'])
        )
        .forEach(item => {
          switch (item.filter.color) {
            case 'white':
              acc.white++;
              break;
            case 'dark_grey':
              acc.darkGrey++;
              break;
            default:
              break;
          }
        });
      return acc;
    },
    { white: 0, darkGrey: 0 }
  );
}

function expandChain(chain) {
  const startWidth = geometry.surround(chain).width;
  let moveTo = Math.max(chain[0].x, 0);
  const newChain = chain.map(link => {
    const movedLink = {
      ...link,
      x: moveTo,
    };
    moveTo = movedLink.x + movedLink.width;
    return movedLink;
  });

  return {
    startWidth,
    chain: newChain,
  };
}
function moveChain(chain, move) {
  return chain.map(link => ({
    ...link,
    x: link.x + move,
  }));
}

function colorLink(link, color) {
  const swappables = productService.getSwappables(link, {
    color: color,
    width: link.width,
  });

  return {
    ...link,
    ...swappables?.[0],
  };
}

function colorChain(chain, sections) {
  const collider = {
    x: chain[0].x,
    y: chain[0].y,
    height: 1,
    width: geometry.surround(chain).width,
  };

  const touchedSections = sections.filter(section =>
    geometry.collides(section, collider, { front: 100 })
  );

  const colorCount = getColorCount(touchedSections);
  const color = colorCount.darkGrey > colorCount.white ? 'dark_grey' : 'white';

  return chain.map(link => colorLink(link, color));
}

function centerChain(expand, sections) {
  const collider = {
    x: expand.chain[0].x,
    y: expand.chain[0].y,
    height: 1,
    width: expand.startWidth,
  };

  const touchedSections = sections.filter(section =>
    geometry.collides(section, collider, { front: 100 })
  );

  const expandedWidth = geometry.surround(expand.chain).width;
  const touchedWidth = geometry.surround(touchedSections).width;
  const sideExtension = floor((expandedWidth - touchedWidth) / 2, 1);

  return moveChain(expand.chain, -sideExtension);
}

function dodgeWall(chain, wallWidth) {
  if (chain[0].x < 0) {
    return moveChain(chain, -chain[0].x);
  }
  const [rightMostLink] = chain.slice(-1);
  const rightMostPos = rightMostLink.x + rightMostLink.width;

  if (rightMostPos > wallWidth) {
    return moveChain(chain, wallWidth - rightMostPos);
  }
  return chain;
}

function getChainSections(chain, sections) {
  const collider = {
    x: chain[0].x,
    y: chain[0].y,
    height: 1,
    width: geometry.surround(chain).width,
  };

  return sections.filter(section =>
    geometry.collides(section, collider, { front: 100 })
  );
}

function overlapOK(overlap, chain, cand, remaining, sections) {
  if (overlap.width >= WIDTH_DIFF_ON_MERGE && cand === remaining[0]) {
    return true;
  }

  const touchedSections = getChainSections(cand, sections);
  const sectionsMaxX = tacHelpers.getLimits({ items: touchedSections }).max.x;
  const currentMaxX = tacHelpers.getLimits({ items: chain }).max.x;
  const inBetweeners = remaining.filter(
    other => other[0].x > chain[0].x && other[0].x < sectionsMaxX
  );

  const inBetweenWidth = tacHelpers.getCondensedTacWidth({
    items: flatten(inBetweeners),
  });

  return currentMaxX + inBetweenWidth - WIDTH_DIFF_ON_MERGE >= sectionsMaxX;
}

function getMergeBuddy(chain, remaining, overlap, sections) {
  if (chain[chain.length - 1].width === SHORT_RAIL_WIDTH) {
    return remaining.find(
      cand =>
        chain !== cand &&
        cand.find(link => link.width === SHORT_RAIL_WIDTH) &&
        overlapOK(overlap, chain, cand, remaining, sections)
    );
  }
}

function mergeBetweenChains(chain, mergeBuddy) {
  const link = chain.pop();
  const sharedRail = getProduct(
    constants.MOUNTING_RAILS[_.camelCase(link.filter.color)][LONG_RAIL_WIDTH]
  );
  chain.push({
    ...sharedRail,
    x: link.x,
    y: MOUNTING_RAIL_Y_POS,
    z: 0,
  });

  const linkBuddyIdx = mergeBuddy.findIndex(
    link => link.width === SHORT_RAIL_WIDTH
  );
  mergeBuddy.splice(linkBuddyIdx, 1);
}

function mergeWithinChain(chain) {
  const totalWidth = chain.reduce((acc, curr) => acc + curr.width, 0);
  const widthNeeded = chain[chain.length - 1].x + SECTION_WIDTH - chain[0].x;
  let excess = totalWidth - widthNeeded;
  return chain
    .map((link, index, links) => {
      if (excess < WIDTH_DIFF_ON_MERGE || link.width > SHORT_RAIL_WIDTH) {
        return link;
      }
      const mergeBuddyIdx = links.find(
        (other, otherIdx) =>
          other.width === SHORT_RAIL_WIDTH && otherIdx > index
      );

      if (!mergeBuddyIdx) {
        return link;
      }

      const sharedRail = getProduct(
        constants.MOUNTING_RAILS[_.camelCase(link.filter.color)][
          LONG_RAIL_WIDTH
        ]
      );
      links.splice(mergeBuddyIdx, 1);
      excess -= WIDTH_DIFF_ON_MERGE;

      return {
        ...sharedRail,
        x: link.x,
        y: MOUNTING_RAIL_Y_POS,
        z: 0,
      };
    })
    .filter(Boolean);
}

function sortChains(chains) {
  // sort all links in the chains
  chains.forEach(chain => chain.sort((a, b) => a.x - b.x));
  // ..and the chains themselves
  chains.sort((c1, c2) => c1[0].x - c2[0].x);
}

function adjustOverlaps(overlapping, wallWidth, sections) {
  /*
  the strategy is:
    - adjust all overlaps to the right,
    - merge all 650s to 1250s if possible
    - if it doesn't fit, do the same thing to the left
    - give up
  */
  let remainingChains = overlapping.slice();
  if (remainingChains.length < 2) {
    return remainingChains;
  }

  const leftAdjusted = [];

  leftAdjusted.push(remainingChains[0]);
  let chain = remainingChains.shift();

  while (remainingChains.length) {
    if (!chain.length) {
      chain = remainingChains.shift();
    }
    let left = geometry.surround(chain);
    const next = remainingChains[0];
    let right = geometry.surround(next);
    let overlap = geometry.intersectingRect(left, right);
    if (overlap.width > 0) {
      const mergeBuddy = getMergeBuddy(
        chain,
        remainingChains,
        overlap,
        sections
      );
      if (mergeBuddy) {
        mergeBetweenChains(chain, mergeBuddy);

        if (!next.length) {
          // the next chain only had a single member, that has now been merged to the current chain
          remainingChains.shift();
        } else {
          // re-calculate overlap post-merge
          left = geometry.surround(chain);
          right = geometry.surround(next);
          overlap = geometry.intersectingRect(left, right);
        }
      }
      if (next.length) {
        remainingChains[0] = moveChain(remainingChains[0], overlap.width);
      }
    }
    if (next.length) {
      leftAdjusted.push(remainingChains[0]);
      chain = remainingChains.shift();
      left = geometry.surround(chain);
    }
    remainingChains = remainingChains.filter(chain => chain.length);
  }

  remainingChains = leftAdjusted.slice();
  let rightAdjusted = [];

  chain = remainingChains[remainingChains.length - 1];
  let right = geometry.surround(chain);
  right.x = wallWidth;
  right.width = 10000;

  while (remainingChains.length) {
    const next = remainingChains[remainingChains.length - 1];
    let left = geometry.surround(next);
    let overlap = geometry.intersectingRect(left, right);
    if (overlap.width > 0) {
      const mergeBuddy = getMergeBuddy(next, rightAdjusted, overlap, sections);
      if (mergeBuddy) {
        const buddyIndex = rightAdjusted.indexOf(mergeBuddy);
        mergeBetweenChains(next, mergeBuddy);
        rightAdjusted = rightAdjusted.map((chain, index) =>
          index <= buddyIndex ? moveChain(chain, SHORT_RAIL_WIDTH) : chain
        );
        chain = rightAdjusted[0];
        if (chain.length) {
          left = geometry.surround(next);
          right = geometry.surround(chain);
          overlap = geometry.intersectingRect(left, right);
        }
      }
      if (chain.length) {
        remainingChains[remainingChains.length - 1] = moveChain(
          remainingChains[remainingChains.length - 1],
          -overlap.width
        );
      }
    }

    rightAdjusted.unshift(remainingChains[remainingChains.length - 1]);
    chain = remainingChains.pop();
    right = geometry.surround(chain);
    rightAdjusted = rightAdjusted.filter(chain => chain.length);
  }

  const finalOverlap = rightAdjusted[0][0].x;
  if (finalOverlap < 0) {
    const move = Math.abs(finalOverlap);
    return rightAdjusted.map(chain => moveChain(chain, move));
  }

  return rightAdjusted;
}

/*
  when we enter here, all the mountingrails are positioned
  on top of their respective sections.
  our objective is:
  - pull them apart so they dont overlap
  - center the chain relative its sections and paint it correctly
  - move it away from any walls it overlaps
  - move it away from other overlapping chains
  */

function getAdjustedMountingRails(mountingRails, sections, wallWidth) {
  if (!mountingRails.length) {
    return [];
  }

  // consider rails right next to each other part of the same chain
  const padding = {
    right: 1,
    left: 1,
  };

  const chains = getMountingRailChains(mountingRails, padding);
  sortChains(chains);

  // merge double shorter rails to longer version where applicable
  const merged = chains.map(mergeWithinChain);

  // expand the chains to their full width
  const expanded = merged.map(expandChain);

  // center them over 'their' sections
  const centeredChains = expanded.map(exp => centerChain(exp, sections));

  // dodge walls
  const fittedChains = centeredChains.map(chain => dodgeWall(chain, wallWidth));

  if (fittedChains.length === 1) {
    // a single chain does not overlap, only coloring left
    const colored = fittedChains.map(chain => colorChain(chain, sections));

    return colored;
  }

  // fix eventual overlaps, if possible
  const adjusted = adjustOverlaps(fittedChains, wallWidth, sections);

  // links might now have merged between chains, so we need to re-calculate them
  const newChains = getMountingRailChains(flatten(adjusted), padding);
  sortChains(newChains);

  // finally, let's color them according to which sections they touch
  const colored = newChains.map(chain => colorChain(chain, sections));

  return colored;
}

export const __test__ = {
  getAdjustedMountingRails,
  adjustOverlaps,
};

function createMountingRail(sections, index, isShared) {
  const MOUNTING_RAIL_Y_POS = 1975;
  // Use white rail for now, we will decide on color in a later step
  const mountingRailProduct =
    getProduct(constants.MOUNTING_RAILS.white[isShared ? '1250' : '650']) ||
    // If white is unavailable on market, use the grey one
    getProduct(constants.MOUNTING_RAILS.darkGrey[isShared ? '1250' : '650']);

  return {
    ...mountingRailProduct,
    x: sections[index].x,
    y: MOUNTING_RAIL_Y_POS,
    z: 0,
  };
}

function useSharedRail(sections, index) {
  return (
    index + 1 < sections.length &&
    sections[index + 1].x + sections[index + 1].width - sections[index].x <
      LONG_RAIL_WIDTH
  );
}

export default function getMountingRails(tac) {
  const sections = tac.items
    .filter(item => productService.isType(item, ITEMS.SECTION))
    .sort((a, b) => a.x - b.x);

  const mountingRails = [];

  for (let i = 0; i < sections.length; i++) {
    if (useSharedRail(sections, i)) {
      mountingRails.push(createMountingRail(sections, i, true));
      i++; // skip next section that is now covered by this rail
    } else {
      mountingRails.push(createMountingRail(sections, i, false));
    }
  }

  const adjustedMountingRails = getAdjustedMountingRails(
    mountingRails.slice(),
    sections,
    geometry.surround(tac.wall.points).width
  );

  return flatten(adjustedMountingRails);
}
